import { Injectable } from '@angular/core';

import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';

import { ValidationStatus } from '@barthauer/abc/ui-components';
import { Measure, SubProject } from '@barthauer/custom/xmt-models';
import { DxNumberBoxComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ROLES } from '../../models/enumeration-list.model';
import { DataDefinitionOptions } from '../../models/home.model';
import { BasedRoleOPathService } from '../../services/based-role-opath.service';
import { DataService, ObjectKeys } from '../../services/data.service';
import { ObjectHelper } from '../../shared/helpers';
import { Persons, StuffMemberPerson } from './measure.model';

@Injectable({
  providedIn: 'root',
})
export class MeasureService {
  public selectedMeasures: Measure[];
  public cachedColumnsResponse: Column[];
  public collaboratorsColumns: Column[] = [];
  public collaboratorsTotalTimeColumns: Column[] = [];
  private calculateTotalTime = new BehaviorSubject<boolean>(false);
  public calculateTotalTime$ = this.calculateTotalTime.asObservable();
  private newStatusReport = new BehaviorSubject<boolean>(false);
  public newStatusReport$ = this.newStatusReport.asObservable();
  public isCreatingSubTask: boolean;
  public staffMembersDataSource: StuffMemberPerson[] = [];
  public measureResponsibleDataSource: StuffMemberPerson[] = [];

  public ROLES = ROLES;
  public degree = [
    { id: 0, value: '0%' },
    { id: 25, value: '25%' },
    { id: 50, value: '50%' },
    { id: 75, value: '75%' },
    { id: 100, value: '100%' },
  ];
  public currentMeasureId: string;
  public currentMeasureIndex: number = 0;
  public measureStatus = [
    { name: 'Erledigt', value: 'Erledigt' },
    { name: 'In Arbeit', value: 'In Arbeit' },
    { name: 'Offen', value: 'Offen' },
  ];
  public maturityLevel: DataDefinitionOptions[] = [
    { name: 'HG1', value: 'HG1' },
    { name: 'HG2', value: 'HG2' },
    { name: 'HG3', value: 'HG3' },
    { name: 'HG4', value: 'HG4' },
    { name: 'HG5', value: 'HG5' },
  ];
  public statusPercentage: DataDefinitionOptions[] = [
    { name: '0%', value: 0 },
    { name: '20%', value: 20 },
    { name: '40%', value: 40 },
    { name: '60%', value: 60 },
    { name: '80%', value: 80 },
    { name: '100%', value: 100 },
  ];
  public readonly measureDesignationColumns = [
    this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.None),
  ];

  constructor(
    private dataService: DataService,
    private coreDataService: CoreDataService,
    private persistenceService: PersistenceService,
    private opathService: BasedRoleOPathService
  ) { }

  public calculateOverAllTime(measure) {
    const collaboratorsList = [];
    if (Array.isArray(measure?.MeasureTasks) && measure.MeasureTasks.length > 0) {
      measure.MeasureTasks.forEach((task) => {
        if (task.Collaborators?.length > 0) {
          task.Collaborators.forEach((collaborator) => {
            collaboratorsList.push(collaborator);
          });
        }
      });
    }
    const unStructuredCollaboratorList = ObjectHelper.groupByListItems(collaboratorsList, 'IdRefMember', 'TimePlanned');
    const structuredCollaboratorList = [];
    Object.keys(unStructuredCollaboratorList).map(function (key) {
      const sum = unStructuredCollaboratorList[key].reduce((a, b) => a + b, 0);
      unStructuredCollaboratorList[key] = { name: key, sum: sum };
      structuredCollaboratorList.push(unStructuredCollaboratorList[key]);
    });
    return structuredCollaboratorList;
  }

  public realTimecalculateTotalTime(data: boolean) {
    this.calculateTotalTime.next(data);
  }
  public setNewSatusReport(statusReport: boolean) {
    this.newStatusReport.next(statusReport);
  }
  public validateNumberField(field: DxNumberBoxComponent, minVal: number, maxVal: number) {
    if (field && field.value) {
      if (field.value >= minVal && field.value <= maxVal) {
        field.validationStatus = ValidationStatus.Valid;
      } else {
        field.validationStatus = ValidationStatus.Invalid;
      }
    }
  }

  public updateMeasure(columnName: string, value, allowUpdate: boolean): void {
    if (allowUpdate === true) {
      const measurePersistQuery: TargetObjectData = new TargetObjectData();
      measurePersistQuery.ObjectKey = new ObjectKey();
      measurePersistQuery.ObjectKey.ObjectType = ObjectKeys.MEASURE;
      const measureColumns: TargetColumnValue[] = [{ Name: columnName, Value: value }];
      measurePersistQuery.Mode = PersistMode.Update;
      measurePersistQuery.ObjectKey.Id = this.currentMeasureId;
      measurePersistQuery.TargetColumns = measureColumns;
      const persistObject: PersistObjectModel = new PersistObjectModel();
      persistObject.Object = measurePersistQuery;
      this.persistenceService.executePersistObjectQuery(persistObject).subscribe();
    }
  }

  public prepareMeasuresByProjectId(measureColumns: QueryColumn[]) {
    return this.dataService
      .readObjects<[]>(ObjectKeys.MEASURE, [
        this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      ])
      .pipe(
        switchMap(() => {
          const subProjectColumns = [
            this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
            this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.None),
          ];
          const subProjectOPath = 'ParentId=' + "'" + localStorage.getItem('projectId') + "'";
          return this.dataService
            .readObjects<SubProject[]>(ObjectKeys.SUBPROJECT, subProjectColumns, subProjectOPath)
            .pipe(
              switchMap(() => {
                const measureOPath = this.checkMeasureOPath();
                return this.dataService
                  .readObjects<Measure[]>(ObjectKeys.MEASURE, measureColumns, measureOPath)
                  .pipe(map((measures) => measures));
              })
            );
        })
      );
  }
  public checkMeasureOPath(): string {
    return this.opathService.checkMeasureOPath();
  }
  public prepareMeasures(measureColumns: QueryColumn[]) {
    const measureOPath = this.checkMeasureOPath();
    const measures: Query = new Query();
    measures.Columns = measureColumns;
    measures.ObjectType = ObjectKeys.MEASURE;
    measures.OPath = measureOPath;
    const measureResources: Query = new Query();
    measureResources.Name = 'MeasureResources';
    measureResources.OPath = 'MeasureResources';
    const measureResourcesColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('IdRefPerson', 'Member'),
      this.coreDataService.createQueryColumn('ref:IdRefPerson', 'Person'),
    ];
    measureResources.Columns = measureResourcesColumns;
    measures.ObjectQueries = [measureResources];
    const queryObjectModel = new QueryObjectsModel();
    queryObjectModel.ObjectQueries = [measures];
    return this.coreDataService.executeReadObjectsQuery(queryObjectModel);
  }

  public persistProperty(ParentId: string, designation: string): Observable<ArrayBuffer> {
    const propertyColumns: TargetColumnValue[] = [];
    const propertyPersistQuery: TargetObjectData = new TargetObjectData();
    propertyPersistQuery.ObjectKey = new ObjectKey();
    propertyPersistQuery.ObjectKey.ObjectType = ObjectKeys.PROPERTY;

    propertyColumns.push({ Name: 'Designation', Value: designation });
    propertyColumns.push({ Name: 'Type', Value: '0' });
    propertyColumns.push({ Name: 'ParentId', Value: ParentId });
    propertyColumns.push({ Name: 'ParentObjectType', Value: ObjectKeys.MEASUREPROGRESS });
    propertyPersistQuery.Mode = PersistMode.Insert;

    propertyPersistQuery.TargetColumns = propertyColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = propertyPersistQuery;
    return this.coreDataService.executePersistObjectQuery(persistObject);
  }

  public getStuffMemberDataSource(persons: Persons[]): StuffMemberPerson[] {
    const staffMember = persons.map(({ FirstName, Id, LastName, AddressPerson }) => ({
      FirstName,
      Id,
      LastName,
      Position: AddressPerson?.[0]?.Position || '',
    }));

    return (this.staffMembersDataSource = staffMember);
  }
  public getMeasureResponsibleDataSource(persons: Persons[]): StuffMemberPerson[] {
    const staffMember = persons.map(({ FirstName, Id, LastName, AddressPerson }) => ({
      FirstName,
      Id,
      LastName,
      Position: AddressPerson?.[0]?.Position || '',
    }));

    return (this.measureResponsibleDataSource = staffMember.filter((t) =>
      [ROLES.MeasureManager, ROLES.CoreTeam, ROLES.SubprojectDeputy, ROLES.SubprojectResponsible].includes(
        t.Position as ROLES
      )
    ));
  }

  public getCoreTeamSupervisorDataSource(persons: Persons[]): StuffMemberPerson[] {
    const staffMember = persons.map(({ FirstName, Id, LastName, AddressPerson }) => ({
      FirstName,
      Id,
      LastName,
      Position: AddressPerson?.[0]?.Position || '',
    }));

    return (staffMember.filter((t) => [ROLES.CoreTeam, ROLES.SteeringCommittee, ROLES.Admin].includes(t.Position as ROLES)));
  }

  public checkAuthorization(): boolean {
    for (let role of this.dataService.userRoles) {
      switch (role) {
        case this.ROLES.MeasureParticipant:
          return true;
        default:
          return false;
      }
    }

    return false;
  }
}
